<template>
  <div class="card border-0 shadow-none rounded-1 mb-25">
    <div class="card-body p-xl-40">
      <a-table
        :columns="columns"
        :data-source="quotations"
        :row-key="'id'"
        :loading="loading"
        :pagination="paginationConfig"
        :scroll="{ x: 1200 }"
        class="text-nowrap align-middle quotation-table"
      >
        <template #emptyText>
          <div class="py-4 text-center text-muted">{{ t("quotation_page.no_quotations_found") }}</div>
        </template>

        <template #bodyCell="{ column, record }">
          <template v-if="column && column.key === 'date'">
            {{ formatDate(record.created_at) }}
          </template>

          <template v-else-if="column && column.key === 'items_count'">
            <span class="badge bg-light text-dark item-count-badge">
              {{ t("quotation_page.items_count", { count: record.items?.length || 0 }) }}
            </span>
          </template>

          <template v-else-if="column && column.key === 'grand_total'">
            ${{ Number(record.grand_total || 0).toFixed(2) }}
          </template>

          <template v-else-if="column && column.key === 'status'">
            <span :class="['status-badge', `status-${String(record.status || 'pending').toLowerCase()}`]">
              {{ formatStatus(record.status) }}
            </span>
          </template>

          <template v-else-if="column && column.key === 'actions'">
            <div class="d-flex justify-content-center gap-2 table-actions">
              <button
                class="btn p-0 border-0 action-icon-btn"
                :title="t('quotation_page.view')"
                data-bs-toggle="modal"
                data-bs-target="#detailsModal"
                @click="$emit('view-details', record)"
              >
                <img src="../../../assets/img/icons/eye.svg" :alt="t('quotation_page.view')" style="width: 14px" />
              </button>
              <button
                class="btn p-0 border-0 action-icon-btn"
                :title="t('quotation_page.create_sale')"
                @click="$emit('create-sale', record)"
              >
                <img src="../../../assets/img/icons/shopping-cart.svg" :alt="t('quotation_page.create_sale')" style="width: 14px" />
              </button>
              <router-link :to="`/edit-quotation/${record.id}`" :title="t('quotation_page.edit')" class="btn p-0 border-0 action-icon-btn">
                <img src="../../../assets/img/icons/edit.svg" :alt="t('quotation_page.edit')" style="width: 14px" />
              </router-link>
              <button
                class="btn p-0 border-0 action-icon-btn"
                :title="t('quotation_page.delete')"
                @click="$emit('delete-quotation', record)"
              >
                <img src="../../../assets/img/icons/close.svg" :alt="t('quotation_page.delete')" style="width: 14px" />
              </button>
            </div>
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import type { QuotationRecord } from "../../../services/quotation";

export default defineComponent({
  name: "QuotationList",
  props: {
    quotations: {
      type: Array as () => QuotationRecord[],
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["view-details", "create-sale", "delete-quotation"],
  setup() {
    const { t, locale } = useI18n();
    const columns = computed(() => [
      { title: t("quotation_page.date"), key: "date", align: "center" as const, width: 220 },
      { title: t("quotation_page.reference"), dataIndex: "reference_no", key: "reference_no", align: "center" as const, width: 150 },
      { title: t("quotation_page.customer"), dataIndex: "customer_name", key: "customer_name", align: "center" as const, width: 190 },
      { title: t("quotation_page.warehouse"), dataIndex: "warehouse_name", key: "warehouse_name", align: "center" as const, width: 180 },
      { title: t("quotation_page.total_items"), key: "items_count", align: "center" as const, width: 140 },
      { title: t("quotation_page.grand_total"), key: "grand_total", align: "center" as const, width: 140 },
      { title: t("quotation_page.status"), key: "status", align: "center" as const, width: 140 },
      { title: t("quotation_page.action"), key: "actions", align: "center" as const, width: 150 },
    ]);

    const paginationConfig = computed(() => ({
      showSizeChanger: true,
      pageSizeOptions: ["10", "20", "30", "50", "100"],
      defaultPageSize: 10,
      locale: { items_per_page: t("quotation_page.items_per_page") },
    }));

    const formatDate = (dateString?: string) => {
      if (!dateString) return t("quotation_page.not_available");
      return new Date(dateString).toLocaleString(locale.value === "km" ? "km-KH" : "en-GB", {
        timeZone: "Asia/Phnom_Penh",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    };

    const formatStatus = (status?: string) => {
      const value = String(status || "pending").toLowerCase();
      return t(`quotation_page.status_${value}`);
    };

    return {
      columns,
      paginationConfig,
      formatDate,
      formatStatus,
      t,
    };
  },
});
</script>

<style scoped>
.quotation-table :deep(.ant-table-cell) {
  vertical-align: middle;
}

.quotation-table :deep(.ant-table-thead > tr > th) {
  white-space: nowrap;
}

.item-count-badge {
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.5rem 0.7rem;
}

.table-actions {
  min-width: 96px;
}

.action-icon-btn {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 90px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.status-pending {
  color: #d97706;
  background: #fff7e6;
}

.status-sent {
  color: #2563eb;
  background: #eff6ff;
}

.status-completed {
  color: #1f9254;
  background: #e8f7ee;
}
</style>
